import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Briefcase, KanbanSquare, Users, CalendarCheck, BarChart2, UserPlus } from "lucide-react";


import OffresEmploi from "./OffresEmploi";
import PipelineKanban from "./PipelineKanban";
import CandidatsFiche from "./CandidatsFiche";
import EntretiensPlanning from "./EntretiensPlanning";
import RecrutementAnalytics from "./RecrutementAnalytics";

const TABS = [
  { id: "offres",     icon: Briefcase,     section: "pipeline" },
  { id: "pipeline",   icon: KanbanSquare,  section: "pipeline" },
  { id: "candidats",  icon: Users,         section: "pipeline" },
  { id: "entretiens", icon: CalendarCheck, section: "collaboration" },
  { id: "analytics",  icon: BarChart2,     section: "collaboration" },
];

const SECTIONS = ["pipeline", "collaboration"];

export default function RecruitmentHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "offres";
  const setActiveTab = (tab) => setSearchParams({ tab }, { replace: true });

  const renderContent = () => {
    switch (activeTab) {
      case "offres":     return <OffresEmploi />;
      case "pipeline":   return <PipelineKanban />;
      case "candidats":  return <CandidatsFiche />;
      case "entretiens": return <EntretiensPlanning />;
      case "analytics":  return <RecrutementAnalytics />;
      default:           return <OffresEmploi />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2
            className="text-2xl font-semibold leading-7 sm:text-3xl"
            style={{ color: "var(--lm-ink)", letterSpacing: "-0.5px" }}
          >
            {t("recruitmentHub.title")}
          </h2>
          <p className="mt-1 text-sm" style={{ color: "var(--lm-ink-2)" }}>
            {t("recruitmentHub.subtitle")}
          </p>
        </div>
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
          style={{
            color: "#5e6ad2",
            backgroundColor: "rgba(94,106,210,0.10)",
            border: "1px solid rgba(94,106,210,0.20)",
          }}
        >
          <UserPlus className="h-3.5 w-3.5" />
          {t("recruitmentHub.badge")}
        </span>
      </div>

      {/* Nav card */}
      <div
        className="rounded-xl"
        style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}
      >
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {SECTIONS.map((section) => {
              const sectionTabs = TABS.filter((t) => t.section === section);
              const hasActive = sectionTabs.some((t) => t.id === activeTab);

              return (
                <div
                  key={section}
                  className="rounded-xl"
                  style={{
                    backgroundColor: hasActive ? "var(--lm-s2)" : "var(--lm-s1)",
                    border: hasActive
                      ? "1px solid #5e6ad2"
                      : "1px solid var(--lm-border)",
                    borderLeft: hasActive ? "3px solid #5e6ad2" : undefined,
                  }}
                >
                  <div className="p-5 space-y-3">
                    <h4 className="font-semibold text-sm" style={{ color: "var(--lm-ink)" }}>
                      {t(`recruitmentHub.sections.${section}`)}
                    </h4>
                    <div style={{ height: "1px", backgroundColor: "var(--lm-border)" }} />
                    <div className="space-y-1">
                      {sectionTabs.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;
                        return (
                          <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-all text-left text-sm font-medium"
                            style={{
                              backgroundColor: isActive ? "#5e6ad2" : "transparent",
                              color: isActive ? "#ffffff" : "var(--lm-ink-2)",
                              border: isActive ? "1px solid #5e6ad2" : "1px solid transparent",
                            }}
                            onMouseEnter={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.backgroundColor = "rgba(94,106,210,0.08)";
                                e.currentTarget.style.color = "var(--lm-ink)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.backgroundColor = "transparent";
                                e.currentTarget.style.color = "var(--lm-ink-2)";
                              }
                            }}
                          >
                            <Icon className="h-4 w-4 flex-shrink-0" />
                            <span className="flex-1">{t(`recruitmentHub.tabs.${tab.id}.label`)}</span>
                            <span className="text-xs" style={{ color: isActive ? "rgba(255,255,255,0.7)" : "var(--lm-ink-3)" }}>
                              {t(`recruitmentHub.tabs.${tab.id}.desc`)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ height: "1px", backgroundColor: "var(--lm-border)", marginBottom: "24px" }} />

          {/* Active tab content */}
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

